import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useRouteError } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';

const safeJSONStringify = (obj: any) => {
    try {
        return JSON.stringify(obj);
    } catch (e) {
        return String(obj);
    }
};

/**
 * Fallback UI for React Router route-level errors (e.g. failed dynamic imports).
 * Use as errorElement on routes.
 */
export function RouteErrorFallback() {
    const error = useRouteError();

    const message = error instanceof Error ? error.message : safeJSONStringify(error);
    const isChunkLoadError =
        typeof message === 'string' && (message.includes('Failed to fetch dynamically imported module') || message.includes('Loading chunk'));

    const handleReload = () => window.location.reload();
    const handleGoHome = () => (window.location.href = '/');

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
            <Card className="w-full max-w-6xl shadow-lg">
                <CardHeader className="space-y-1 border-b bg-destructive/5 pb-6">
                    <div className="flex items-center gap-3">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                            <AlertTriangle className="size-6 text-destructive" />
                        </div>
                        <div>
                            <CardTitle className="text-xl">Something went wrong</CardTitle>
                            <CardDescription className="mt-0.5">
                                {isChunkLoadError
                                    ? 'The page failed to load. This can happen after a deployment or due to a network issue.'
                                    : 'We encountered an unexpected error. Please try one of the options below.'}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-5 pt-6">
                    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                        <p className="font-medium text-destructive">Error</p>
                        <p className="mt-1 font-mono text-sm text-foreground">{message}</p>
                        {import.meta.env.DEV && error instanceof Error && error.stack && (
                            <details className="mt-4 min-w-0 overflow-hidden">
                                <summary className="cursor-pointer select-none font-medium text-muted-foreground text-sm transition-colors hover:text-foreground">
                                    View stack trace (development only)
                                </summary>
                                <pre className="mt-3 max-h-96 min-w-0 max-w-full overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
                                    {error.stack}
                                </pre>
                            </details>
                        )}
                    </div>

                    <div className="rounded-lg border bg-muted/30 p-4">
                        <p className="font-medium text-sm">What you can do</p>
                        <ul className="mt-2 space-y-1.5 text-muted-foreground text-sm">
                            <li className="flex items-center gap-2">
                                <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                Refresh the page to try again
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                Go back to the home page
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                Contact support if the problem persists
                            </li>
                        </ul>
                    </div>
                </CardContent>
                <CardFooter className="flex flex-wrap gap-2 border-t bg-muted/20 pt-6">
                    <Button onClick={handleReload} variant="primary" size="sm" dataTestId="ram-route-error-reload">
                        <RefreshCw className="mr-2 size-4" />
                        Reload Page
                    </Button>
                    <Button onClick={handleGoHome} variant="outline" size="sm" dataTestId="ram-route-error-go-home">
                        <Home className="mr-2 size-4" />
                        Go Home
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
